import React, { useState, useEffect } from 'react';
import { tenantNurseAPI } from '../../../services/api/tenantNurseAPI';
import { useAuth } from '../../../services/contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  UserCheck, 
  Heart, 
  Activity, 
  Clock, 
  AlertTriangle,
  Send,
  FileText,
  Users,
  TrendingUp,
  CheckCircle,
  Plus
} from 'lucide-react';

const TenantNurseDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    total_patients: 0,
    recent_vitals: 0,
    concerning_vitals: 0,
    my_care_notes: 0,
    nurse_name: '',
    patients_change: 0,
    vitals_change: 0,
    concerning_change: 0,
    notes_change: 0
  });
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryResponse, patientsResponse, alertsResponse] = await Promise.all([
        tenantNurseAPI.getDashboardSummary(),
        tenantNurseAPI.getPatients(),
        tenantNurseAPI.getCriticalAlerts()
      ]);
      
      setDashboardData(summaryResponse.data);
      setRecentPatients(patientsResponse.data.slice(0, 5)); // Show only first 5 patients
      setCriticalAlerts(alertsResponse.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "blue", change = null, alert = false }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 ${
      alert ? 'border-l-red-500' : `border-l-${color}-500`
    } border-r border-t border-b border-gray-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold mt-2 ${
            alert ? 'text-red-600' : `text-${color}-600`
          }`}>{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? '+' : ''}{change}% from yesterday
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${
          alert ? 'bg-red-100' : `bg-${color}-100`
        }`}>
          <Icon className={`h-6 w-6 ${
            alert ? 'text-red-600' : `text-${color}-600`
          }`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nursing Dashboard</h1>
          <p className="text-gray-600">Welcome back, {dashboardData.nurse_name || user?.name}</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={dashboardData.total_patients}
          icon={UserCheck}
          color="blue"
          change={dashboardData.patients_change}
        />
        <StatCard
          title="Recent Vitals (24h)"
          value={dashboardData.recent_vitals}
          icon={Heart}
          color="green"
          change={dashboardData.vitals_change}
        />
        <StatCard
          title="Concerning Vitals"
          value={dashboardData.concerning_vitals}
          icon={AlertTriangle}
          alert={dashboardData.concerning_vitals > 0}
          change={dashboardData.concerning_change}
        />
        <StatCard
          title="My Care Notes"
          value={dashboardData.my_care_notes}
          icon={FileText}
          color="va-primary-600"
          change={dashboardData.notes_change}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Patient Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Patient Activity</h3>
              <button className="text-sm text-green-600 hover:text-green-800 font-medium">
                View All Patients →
              </button>
            </div>
          </div>
          <div className="p-6">
            {recentPatients.length === 0 ? (
              <div className="text-center py-8">
                <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent patient activity</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPatients.map((patient) => (
                  <div key={patient.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{patient.alias}</p>
                        <p className="text-sm text-gray-600">
                          {patient.latest_vital ? 
                            `Last vitals: ${new Date(patient.latest_vital.created_at).toLocaleDateString()}` :
                            'No recent vitals'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {patient.latest_vital && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          patient.latest_vital.quality_flag === 'High' ? 'bg-green-100 text-green-800' :
                          patient.latest_vital.quality_flag === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {patient.latest_vital.quality_flag || 'Normal'}
                        </span>
                      )}
                      <button 
                        className="p-2 text-gray-400 hover:text-green-600"
                        title="Send Recording Link"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Alerts */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <button className="w-full flex items-center space-x-3 p-3 text-left bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
                  <Send className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">Send Recording Links</p>
                    <p className="text-sm text-gray-600">Email or SMS to patients</p>
                  </div>
                </button>
                <button className="w-full flex items-center space-x-3 p-3 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Add Care Notes</p>
                    <p className="text-sm text-gray-600">Document patient care</p>
                  </div>
                </button>
                <button className="w-full flex items-center space-x-3 p-3 text-left bg-va-primary-50 hover:bg-va-primary-100 rounded-lg transition-colors">
                  <Heart className="h-5 w-5 text-va-primary-600" />
                  <div>
                    <p className="font-medium text-gray-900">Review Vitals</p>
                    <p className="text-sm text-gray-600">View patient recordings</p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Critical Alerts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Critical Alerts</h3>
              </div>
            </div>
            <div className="p-6">
              {criticalAlerts.length > 0 ? (
                <div className="space-y-3">
                  {criticalAlerts.map((alert, index) => (
                    <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg ${
                      alert.type === 'critical' ? 'bg-red-50' : 'bg-orange-50'
                    }`}>
                      <AlertTriangle className={`h-5 w-5 ${
                        alert.type === 'critical' ? 'text-red-600' : 'text-orange-600'
                      }`} />
                      <div>
                        <p className={`font-medium ${
                          alert.type === 'critical' ? 'text-red-900' : 'text-orange-900'
                        }`}>
                          {alert.title}
                        </p>
                        <p className={`text-sm ${
                          alert.type === 'critical' ? 'text-red-700' : 'text-orange-700'
                        }`}>
                          {alert.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">All Clear</p>
                  <p className="text-sm text-gray-600">No critical alerts at this time</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantNurseDashboard;